<script setup lang="ts">
import { computed, ref } from "vue";

import { getPropertyConfig, getPropertyWithConfigList, showPropertiesWithConfig } from "~/common/properties";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import GenericDropdownItem from "~/components/GenericDropdownItem.vue";
import { NOT_SHOWN_PROPERTY_KINDS } from "~/constants/property";
import { CheckIcon, LockIcon, PropertiesIcon, ShowIcon } from "~/icons";
import { DropdownMenuItemKind } from "~/shared/enums";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { makePropertyComparator } from "~/utils/comparator";

import PropertyDefaultEditor from "./topbar/PropertyDefaultEditor.vue";

defineProps<{
  shownProperties?: boolean;
  defaultProperties?: boolean;
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const searchValue = ref("");

const propertyComparator = computed(() => makePropertyComparator(appStore.propertyOrderDuids));

const allProperties = computed(() =>
  dataStore.propertyList
    .map((p) => ({ ...p, config: getPropertyConfig(p.kind) }))
    .sort((a, b) => {
      if (a.hidden || b.hidden) {
        return 1;
      }
      return propertyComparator.value(a.duid, b.duid);
    })
);

const shownPropertiesWithConfig = computed(() => showPropertiesWithConfig(appStore.propertyOrderDuids));

const visiblePropertyDuids = computed(
  () => new Set(shownPropertiesWithConfig.value.filter(({ hidden }) => !hidden).map(({ property }) => property.duid))
);

const lockedPropertyDuids = computed(
  () => new Set(shownPropertiesWithConfig.value.filter(({ locked }) => locked).map(({ property }) => property.duid))
);

const toggleShownProperty = (propertyDuid: string, shown: boolean) => {
  if (lockedPropertyDuids.value.has(propertyDuid)) {
    return;
  }
  const propertyOrderDuids = new Set(appStore.propertyOrderDuids);

  if (shown) {
    propertyOrderDuids.add(propertyDuid);
  } else {
    propertyOrderDuids.delete(propertyDuid);
  }

  appStore.setPropertyDuidVisibility(propertyDuid, shown, Array.from(propertyOrderDuids));
};

const allPropertiesSections = computed(() => {
  const filteredProperties = allProperties.value.filter(
    (property) =>
      !NOT_SHOWN_PROPERTY_KINDS.has(property.kind) &&
      (!searchValue.value.trim() || property.title.toLowerCase().includes(searchValue.value.toLowerCase()))
  );

  return [
    {
      title: "Shown properties",
      items: filteredProperties
        .map((property) => {
          const isVisible = visiblePropertyDuids.value.has(property.duid);
          const isLocked = lockedPropertyDuids.value.has(property.duid);

          return {
            property,
            isLocked,
            title: property.title,
            kind: DropdownMenuItemKind.COMPONENT,
            component: GenericDropdownItem,
            componentArgs: {
              title: property.title,
              icon: property.config.icon,
              iconArgs: { class: "icon-sm" },
              iconAfter: isLocked ? LockIcon : isVisible ? CheckIcon : undefined,
              iconAfterArgs: { class: `icon-md ${isLocked ? "text-vlt" : "text-primary-base"}` },
              disabled: isLocked,
              onClick: () => toggleShownProperty(property.duid, !isVisible),
            },
          };
        })
        .sort((a, b) => {
          if (a.isLocked !== b.isLocked) {
            return a.isLocked ? -1 : 1;
          }
          const aIsVisible = visiblePropertyDuids.value.has(a.property.duid);
          const bIsVisible = visiblePropertyDuids.value.has(b.property.duid);
          if (aIsVisible !== bIsVisible) {
            return aIsVisible ? -1 : 1;
          }
          return propertyComparator.value(a.property.duid, b.property.duid);
        }),
    },
  ];
});

const currentDartboard = computed(() =>
  appStore.currentDartboardOrDefault ? dataStore.getDartboardByDuid(appStore.currentDartboardOrDefault.duid) : undefined
);

const sortedPropertyList = computed(() =>
  getPropertyWithConfigList()
    .filter(
      ([property, config]) =>
        !config.readOnly && !NOT_SHOWN_PROPERTY_KINDS.has(property.kind) && config.propertyDefault !== null
    )
    .map(([property]) => property)
    .sort((a, b) => propertyComparator.value(a.duid, b.duid))
);

const shownPropertyList = computed(() => {
  if (visiblePropertyDuids.value.size === 0) {
    return sortedPropertyList.value;
  }
  return sortedPropertyList.value.filter((property) => visiblePropertyDuids.value.has(property.duid));
});

const propertyDropdownSections = computed(() => {
  if (!currentDartboard.value) {
    return [];
  }
  const dartboard = currentDartboard.value;
  return [
    {
      title: "Default properties",
      items: shownPropertyList.value
        .map((property) => ({
          title: property.title,
          kind: DropdownMenuItemKind.COMPONENT,
          component: PropertyDefaultEditor,
          componentArgs: {
            dartboard,
            propertyDuid: property.duid,
            value: dartboard.defaultPropertyMap[property.duid] ?? null,
          },
        }))
        .sort((a, b) => {
          const aHasValue = a.componentArgs.value !== null && a.componentArgs.value !== undefined;
          const bHasValue = b.componentArgs.value !== null && b.componentArgs.value !== undefined;

          if (aHasValue !== bHasValue) {
            return aHasValue ? -1 : 1;
          }

          return propertyComparator.value(a.componentArgs.propertyDuid, b.componentArgs.propertyDuid);
        }),
    },
  ];
});
</script>

<template>
  <DropdownMenu
    v-if="pageStore.isMobile && shownProperties"
    header-title="Shown properties"
    show-search
    mobile-include-done
    prevent-close-on-select
    :sections="allPropertiesSections">
    <DropdownMenuItemContent :icon="ShowIcon" title="Shown properties" class="py-3 text-md" is-submenu />
  </DropdownMenu>
  <DropdownMenu
    v-if="pageStore.isMobile && defaultProperties"
    :sections="propertyDropdownSections"
    header-title="Defaults"
    show-search
    mobile-include-done
    prevent-close-on-select>
    <DropdownMenuItemContent :icon="PropertiesIcon" title="Default properties" class="py-3 text-md" is-submenu />
  </DropdownMenu>
</template>
